import { format, parseISO } from 'date-fns';
import { fr } from 'date-fns/locale';
import {
  Check,
  ChevronDown,
  Eye,
  X
} from 'lucide-react';
import { useMemo, useState } from 'react';
import useGrandeEcole from '../../stores/useGrandeEcole';


export function ModerationZone({ darkMode = true, isEditable = false, onSave }) {
  const {
    learners = [],
    cohortes = [],
    loading,
  } = useGrandeEcole();

  // États locaux
  const [viewMode, setViewMode] = useState('critical'); // 'critical' | 'all'
  const [filterType, setFilterType] = useState('all');  // 'all' | 'décrochage' | 'inactif' | 'low_progress'
  const [sortBy, setSortBy] = useState('severity');     // 'severity' | 'progress' | 'name'

  // GÉNÉRATION ALERTES DEPUIS LEARNERS RÉELS
  const alerts = useMemo(() => {
    return learners
      .map(learner => {
        const alerts = [];
        const progress = learner.globalProgress || 0;
        const status = learner.status?.toLowerCase();
        const lastActivity = learner.lastActivityAt || learner.lastLogin;
        
        // Trouver cohorte du learner (si applicable)
        const cohorte = cohortes.find(c => 
          c.learners?.some(l => l === learner._id || l === learner.userId)
        );

        // 🚨 ALERTE CRITIQUE : Décrochage (progress < 20% ET inactif)
        if (progress < 20 && status === 'inactive') {
          alerts.push({
            id: `${learner._id}-decrochage`,
            type: 'décrochage',
            severity: 'critical',
            learnerId: learner._id || learner.userId,
            learnerName: `${learner.firstName || learner.prenom || ''} ${learner.lastName || learner.nom || ''}`.trim(),
            cohorte: cohorte?.nom || '—',
            message: `Décrochage critique détecté`,
            details: {
              eleve: `${learner.firstName || learner.prenom} ${learner.lastName || learner.nom}`,
              cohorte: cohorte?.nom || 'Aucune cohorte',
              avgProgress: progress,
              status: status
            },
            timestamp: new Date().toISOString()
          });
        }

        // ⚠️ ALERTE WARNING : Progression faible (< 30%)
        if (progress < 30 && progress >= 20 && status !== 'inactive') {
          alerts.push({
            id: `${learner._id}-low-progress`,
            type: 'progression faible',
            severity: 'warning',
            learnerId: learner._id || learner.userId,
            learnerName: `${learner.firstName || learner.prenom} ${learner.lastName || learner.nom}`,
            cohorte: cohorte?.nom || '—',
            message: `Progression en dessous du seuil (${progress}%)`,
            details: {
              eleve: `${learner.firstName || learner.prenom} ${learner.lastName || learner.nom}`,
              cohorte: cohorte?.nom || 'Aucune cohorte',
              avgProgress: progress,
              status: status
            },
            timestamp: new Date().toISOString()
          });
        }

        // ℹ️ ALERTE INFO : Inactivité longue durée
        if (lastActivity) {
          const daysSinceActivity = Math.floor(
            (Date.now() - new Date(lastActivity).getTime()) / (1000 * 60 * 60 * 24)
          );
          
          if (daysSinceActivity > 7 && status === 'active') {
            alerts.push({
              id: `${learner._id}-inactif`,
              type: 'inactif',
              severity: 'info',
              learnerId: learner._id || learner.userId,
              learnerName: `${learner.firstName || learner.prenom} ${learner.lastName || learner.nom}`,
              cohorte: cohorte?.nom || '—',
              message: `Pas de connexion depuis ${daysSinceActivity} jours`,
              details: {
                eleve: `${learner.firstName || learner.prenom} ${learner.lastName || learner.nom}`,
                cohorte: cohorte?.nom || 'Aucune cohorte',
                avgProgress: progress,
                daysSinceActivity
              },
              timestamp: new Date().toISOString()
            });
          }
        }

        return alerts;
      })
      .flat()
      .filter(Boolean);
  }, [learners, cohortes]);

  // FILTRAGE
  const filteredAlerts = useMemo(() => {
    let filtered = [...alerts];

    // Filtrer par mode (critical = seulement critical/warning, all = tout)
    if (viewMode === 'critical') {
      filtered = filtered.filter(a => 
        a.severity === 'critical' || a.severity === 'emergency'
      );
    }

    // Filtrer par type
    if (filterType !== 'all') {
      filtered = filtered.filter(a => a.type === filterType);
    }

    // Tri
    filtered.sort((a, b) => {
      if (sortBy === 'severity') {
        const severityOrder = { critical: 0, emergency: 0, warning: 1, info: 2 };
        return severityOrder[a.severity] - severityOrder[b.severity];
      }
      if (sortBy === 'progress') {
        return (a.details?.avgProgress || 100) - (b.details?.avgProgress || 100);
      }
      if (sortBy === 'name') {
        return (a.learnerName || '').localeCompare(b.learnerName || '');
      }
      return 0;
    });

    return filtered;
  }, [alerts, viewMode, filterType, sortBy]);

  // STATS
  const criticalCount = alerts.filter(a => 
    a.severity === 'critical' || a.severity === 'emergency'
  ).length;

  const warningCount = alerts.filter(a => a.severity === 'warning').length;

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    try {
      return format(parseISO(dateStr), 'dd/MM/yyyy HH:mm', { locale: fr });
    } catch {
      return dateStr.split('T')[0] || dateStr;
    }
  };

  // ACTIONS
  const handleViewLearner = (learnerId) => {
    // TODO : Ouvrir modal détails learner
    console.log('Voir détails learner:', learnerId);
    alert(`Détails learner ${learnerId} - À implémenter`);
  };

  const handleMarkAsResolved = (alertId) => {
    // TODO : Marquer alerte comme résolue (stocker dans DB)
    console.log('Marquer résolu:', alertId);
    alert(`Alerte ${alertId} marquée comme résolue - À implémenter`);
  };

  const handleIgnoreAlert = (alertId) => {
    // TODO : Ignorer alerte (stocker dans DB)
    console.log('Ignorer alerte:', alertId);
    if (window.confirm('Ignorer cette alerte ?')) {
      alert(`Alerte ${alertId} ignorée - À implémenter`);
    }
  };

  if (loading) {
    return (
      <div className="space-y-8 opacity-50">
        <div className="h-10 bg-zinc-800 animate-pulse rounded" />
        <div className="h-64 bg-zinc-800 animate-pulse rounded" />
      </div>
    );
  }

  return (
  <div className="space-y-8">

    {/* HEADER */}
    <div className="border-b border-zinc-800 pb-4">
      <h3 className="text-xl md:text-2xl font-black tracking-tighter text-rose-400">
        ZONE DE CONTRÔLE & MODÉRATION
      </h3>
      <p className="text-[11px] uppercase tracking-widest text-zinc-500 mt-1">
        {filteredAlerts.length} alertes affichées – {criticalCount} critiques · {warningCount} warnings
      </p>
    </div>

    {/* CONTRÔLES + FILTRES */}
    <div className="flex flex-col gap-3 bg-black/40 border border-zinc-800 p-3 md:p-4">

      {/* Ligne 1 : toggle + selects */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="flex items-center bg-zinc-900/50 border border-zinc-800 rounded p-1">
          <button
            onClick={() => setViewMode('critical')}
            className={`px-3 md:px-4 py-1.5 text-xs font-bold uppercase tracking-wider transition-all ${
              viewMode === 'critical'
                ? 'bg-red-900/60 text-red-300 border border-red-700/50'
                : 'text-zinc-500 hover:text-zinc-300'
            }`}
          >
            Critiques ({criticalCount})
          </button>
          <button
            onClick={() => setViewMode('all')}
            className={`px-3 md:px-4 py-1.5 text-xs font-bold uppercase tracking-wider transition-all ${
              viewMode === 'all'
                ? 'bg-zinc-700 text-zinc-200 border border-zinc-600'
                : 'text-zinc-500 hover:text-zinc-300'
            }`}
          >
            Tout ({alerts.length})
          </button>
        </div>

        <div className="relative">
          <select
            value={filterType}
            onChange={e => setFilterType(e.target.value)}
            className="appearance-none bg-zinc-900 border border-zinc-800 px-3 py-1.5 pr-8 text-xs font-mono text-zinc-300 outline-none focus:border-rose-500/50"
          >
            <option value="all">Tous types</option>
            <option value="décrochage">Décrochage</option>
            <option value="progression faible">Prog. faible</option>
            <option value="inactif">Inactivité</option>
          </select>
          <ChevronDown size={12} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-zinc-500 pointer-events-none" />
        </div>

        <div className="relative">
          <select
            value={sortBy}
            onChange={e => setSortBy(e.target.value)}
            className="appearance-none bg-zinc-900 border border-zinc-800 px-3 py-1.5 pr-8 text-xs font-mono text-zinc-300 outline-none focus:border-rose-500/50"
          >
            <option value="severity">Par sévérité</option>
            <option value="progress">Par progression</option>
            <option value="name">Par nom</option>
          </select>
          <ChevronDown size={12} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-zinc-500 pointer-events-none" />
        </div>
      </div>

      {/* Ligne 2 : stats rapides */}
      <div className="flex flex-wrap items-center gap-3 md:gap-4 text-xs">
        <div className="flex items-center gap-1.5">
          <div className="w-2 h-2 rounded-full bg-red-500 animate-pulse" />
          <span className="text-zinc-500">Critiques:</span>
          <span className="text-red-400 font-bold">{criticalCount}</span>
        </div>
        <div className="flex items-center gap-1.5">
          <div className="w-2 h-2 rounded-full bg-amber-500" />
          <span className="text-zinc-500">Warnings:</span>
          <span className="text-amber-400 font-bold">{warningCount}</span>
        </div>
        <div className="flex items-center gap-1.5">
          <div className="w-2 h-2 rounded-full bg-zinc-500" />
          <span className="text-zinc-500">Info:</span>
          <span className="text-zinc-400 font-bold">{alerts.filter(a => a.severity === 'info').length}</span>
        </div>
      </div>
    </div>

    {/* LISTE ALERTES */}
    {filteredAlerts.length === 0 ? (
      <div className="text-center py-16 text-zinc-500 border border-zinc-800 rounded bg-black/30">
        <div className="text-3xl mb-4 opacity-40">✓</div>
        <p>Aucune alerte {viewMode === 'critical' ? 'critique' : ''} en cours</p>
        <p className="text-[11px] mt-2">
          {viewMode === 'critical'
            ? 'Tout semble sous contrôle pour le moment'
            : 'Aucune alerte générée depuis les données apprenants'}
        </p>
      </div>
    ) : (
      <div className={`border border-zinc-800 overflow-hidden ${darkMode ? 'bg-black/40' : 'bg-white'}`}>

        {/* HEADER TABLE — desktop uniquement */}
        <div className={`hidden md:grid grid-cols-6 gap-4 px-6 py-3 text-[10px] font-black uppercase tracking-widest border-b ${
          darkMode ? 'bg-zinc-900/60 border-zinc-700 text-zinc-400' : 'bg-zinc-100 border-zinc-200 text-zinc-600'
        }`}>
          <div>TYPE</div>
          <div>APPRENANT</div>
          <div>COHORTE</div>
          <div>DÉTAILS</div>
          <div className="text-center">PROGRESSION</div>
          <div className="text-right">ACTIONS</div>
        </div>

        {/* ROWS */}
        <div className="divide-y divide-zinc-800/40 max-h-[600px] overflow-y-auto">
          {filteredAlerts.map((alert) => (
            <div key={alert.id} className="hover:bg-rose-950/10 transition-colors">

              {/* ═══════════════════════════════════ */}
              {/* VUE MOBILE : carte compacte         */}
              {/* ═══════════════════════════════════ */}
              <div className="md:hidden px-4 py-3 space-y-2">

                {/* Ligne 1 : badge + nom + actions */}
                <div className="flex items-start gap-2">
                  <span className={`shrink-0 mt-0.5 px-2 py-0.5 text-[9px] font-bold uppercase border ${
                    alert.severity === 'critical' || alert.severity === 'emergency'
                      ? 'bg-red-900/60 text-red-300 border-red-700/50'
                      : alert.severity === 'warning'
                      ? 'bg-amber-900/50 text-amber-300 border-amber-700/40'
                      : 'bg-zinc-800/50 text-zinc-300 border-zinc-700/40'
                  }`}>
                    {(alert.type || 'INFO').toUpperCase().slice(0, 8)}
                  </span>
                  <div className="flex-1 min-w-0">
                    <div className={`font-medium text-sm truncate ${darkMode ? 'text-zinc-200' : 'text-zinc-800'}`}>
                      {alert.learnerName || alert.details?.eleve || '—'}
                    </div>
                    <div className="text-[10px] text-zinc-500 font-mono truncate">
                      {alert.cohorte || '—'} · {formatDate(alert.timestamp)}
                    </div>
                  </div>
                  <div className="flex items-center gap-0.5 shrink-0">
                    <button onClick={() => handleViewLearner(alert.learnerId)}
                      className="p-1.5 hover:text-rose-400 transition-colors" title="Voir">
                      <Eye size={14} />
                    </button>
                    <button onClick={() => handleMarkAsResolved(alert.id)}
                      className="p-1.5 hover:text-emerald-400 transition-colors" title="Traité">
                      <Check size={14} />
                    </button>
                    <button onClick={() => handleIgnoreAlert(alert.id)}
                      className="p-1.5 hover:text-red-400 transition-colors" title="Ignorer">
                      <X size={14} />
                    </button>
                  </div>
                </div>

                {/* Ligne 2 : message + progression */}
                <div className="flex items-center gap-3">
                  <span className="text-rose-300 text-[11px] flex-1 truncate">
                    {alert.message || '—'}
                    {alert.details?.daysSinceActivity && (
                      <span className="opacity-70"> ({alert.details.daysSinceActivity}j)</span>
                    )}
                  </span>
                  <div className="flex items-center gap-2 shrink-0">
                    <div className="w-14 bg-zinc-800 h-1 overflow-hidden">
                      <div className={`h-full transition-all ${
                        (alert.details?.avgProgress || 0) < 20 ? 'bg-red-500'
                        : (alert.details?.avgProgress || 0) < 50 ? 'bg-amber-500'
                        : 'bg-emerald-500'
                      }`} style={{ width: `${alert.details?.avgProgress || 0}%` }} />
                    </div>
                    <span className="text-[10px] font-mono text-zinc-500 w-7 text-right">
                      {alert.details?.avgProgress || 0}%
                    </span>
                  </div>
                </div>

              </div>

              {/* ═══════════════════════════════════ */}
              {/* VUE DESKTOP md+ : tableau 6 col     */}
              {/* ═══════════════════════════════════ */}
              <div className={`hidden md:grid grid-cols-6 gap-4 px-6 py-4 ${darkMode ? 'text-zinc-300' : 'text-zinc-700'}`}>

                {/* TYPE + SEVERITY */}
                <div className="flex flex-col gap-1">
                  <span className={`inline-flex px-3 py-1 text-[10px] font-bold uppercase tracking-wider border w-fit ${
                    alert.severity === 'critical' || alert.severity === 'emergency'
                      ? 'bg-red-900/60 text-red-300 border-red-700/50'
                      : alert.severity === 'warning'
                      ? 'bg-amber-900/50 text-amber-300 border-amber-700/40'
                      : 'bg-zinc-800/50 text-zinc-300 border-zinc-700/40'
                  }`}>
                    {(alert.type || 'INFO').toUpperCase()}
                  </span>
                  <span className="text-[9px] text-zinc-600 font-mono">{formatDate(alert.timestamp)}</span>
                </div>

                {/* APPRENANT */}
                <div className="flex flex-col gap-1">
                  <div className="font-medium">{alert.learnerName || alert.details?.eleve || '—'}</div>
                  <div className="text-[10px] text-zinc-500 font-mono">ID: {alert.learnerId?.slice(-6) || '—'}</div>
                </div>

                {/* COHORTE */}
                <div className="text-zinc-400 font-mono text-[11px]">
                  {alert.cohorte || alert.details?.cohorte || '—'}
                </div>

                {/* DÉTAILS */}
                <div className="text-rose-300 text-[11px]">
                  {alert.message || 'Aucun détail'}
                  {alert.details?.daysSinceActivity && (
                    <span className="ml-2 opacity-70 block text-[10px]">
                      ({alert.details.daysSinceActivity}j depuis dernière activité)
                    </span>
                  )}
                </div>

                {/* PROGRESSION */}
                <div className="flex flex-col items-center gap-2">
                  <div className="flex items-center gap-2 w-full">
                    <div className="flex-1 bg-zinc-800 h-1.5 overflow-hidden">
                      <div className={`h-full transition-all ${
                        (alert.details?.avgProgress || 0) < 20 ? 'bg-red-500'
                        : (alert.details?.avgProgress || 0) < 50 ? 'bg-amber-500'
                        : 'bg-emerald-500'
                      }`} style={{ width: `${alert.details?.avgProgress || 0}%` }} />
                    </div>
                    <span className="text-[10px] font-mono text-zinc-500 w-8">
                      {alert.details?.avgProgress || 0}%
                    </span>
                  </div>
                  {alert.details?.status && (
                    <span className={`text-[9px] px-2 py-0.5 border ${
                      alert.details.status === 'active'
                        ? 'bg-emerald-900/20 text-emerald-400 border-emerald-700/40'
                        : 'bg-zinc-800 text-zinc-500 border-zinc-700'
                    }`}>
                      {alert.details.status.toUpperCase()}
                    </span>
                  )}
                </div>

                {/* ACTIONS */}
                <div className="flex items-center justify-end gap-2 opacity-70 hover:opacity-100 transition-opacity">
                  <button onClick={() => handleViewLearner(alert.learnerId)}
                    className="p-1.5 hover:text-rose-400 transition-colors" title="Voir détails apprenant">
                    <Eye size={16} />
                  </button>
                  <button onClick={() => handleMarkAsResolved(alert.id)}
                    className="p-1.5 hover:text-emerald-400 transition-colors" title="Marquer comme traité">
                    <Check size={16} />
                  </button>
                  <button onClick={() => handleIgnoreAlert(alert.id)}
                    className="p-1.5 hover:text-red-400 transition-colors" title="Ignorer">
                    <X size={16} />
                  </button>
                </div>

              </div>
            </div>
          ))}
        </div>
      </div>
    )}

    {/* FOOTER STATS */}
    <div className="border border-zinc-800 bg-black/40 p-4">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-xs">
        <div>
          <div className="text-zinc-500 mb-1">Total alertes</div>
          <div className="text-2xl font-black text-zinc-300">{alerts.length}</div>
        </div>
        <div>
          <div className="text-zinc-500 mb-1">Décrochages</div>
          <div className="text-2xl font-black text-red-400">
            {alerts.filter(a => a.type === 'décrochage').length}
          </div>
         </div>
        </div>
      </div>
    </div>
  );
}